import { API_CONFIG } from '@/shared/config/api.config';
import { ApiResponse, ApiError, ApiRequestConfig } from '@/shared/types/api.types';

class ApiClient {
  private baseURL: string;
  private timeout: number;
  private retryAttempts: number;
  private retryDelay: number;

  constructor() {
    this.baseURL = API_CONFIG.BASE_URL;
    this.timeout = API_CONFIG.TIMEOUT;
    this.retryAttempts = API_CONFIG.RETRY_ATTEMPTS;
    this.retryDelay = API_CONFIG.RETRY_DELAY;
  }

  private getAuthToken(): string | null {
    return localStorage.getItem('civista_auth_token');
  }

  private getDefaultHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };

    const token = this.getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  private buildURL(endpoint: string, params?: Record<string, string | number | boolean>): string {
    const url = new URL(endpoint, this.baseURL);
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        url.searchParams.append(key, String(value));
      });
    }

    return url.toString();
  }

  private async handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
    let data: any;

    try {
      const text = await response.text();
      data = text ? JSON.parse(text) : {};
    } catch (error) {
      console.error('Failed to parse response:', error);
      throw new Error('Invalid JSON response');
    }

    if (!response.ok) {
      console.error('API Error Response:', {
        status: response.status,
        statusText: response.statusText,
        data: data
      });

      const error: ApiError = data?.error || {
        code: data?.code || 'HTTP_ERROR',
        message: data?.message || data?.error?.message || `HTTP ${response.status}: ${response.statusText}`,
        timestamp: new Date().toISOString(),
        requestId: crypto.randomUUID(),
      };

      throw error;
    }

    // Handle both nested and flat response structures
    if (data?.data) {
      return data as ApiResponse<T>;
    }
    
    // If the response is already in the expected format, wrap it
    return { data: data as T } as ApiResponse<T>;
  }

  private async delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private async makeRequest<T>(
    endpoint: string,
    config: ApiRequestConfig = {}
  ): Promise<ApiResponse<T>> {
    const {
      method = 'GET',
      headers = {},
      body,
      params,
      timeout = this.timeout,
      retries = this.retryAttempts,
    } = config;

    const url = this.buildURL(endpoint, params);
    const requestHeaders = { ...this.getDefaultHeaders(), ...headers };

    const requestConfig: RequestInit = {
      method,
      headers: requestHeaders,
      signal: AbortSignal.timeout(timeout),
    };

    if (body && method !== 'GET') {
      requestConfig.body = JSON.stringify(body);
    }

    let lastError: unknown;

    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        console.log(`API Request: ${method} ${url}`);
        const response = await fetch(url, requestConfig);
        const result = await this.handleResponse<T>(response);
        console.log('API Response:', result);
        return result;
      } catch (error) {
        lastError = error;

        // Don't retry on certain errors
        if (error instanceof Error) {
          if (error.name === 'AbortError') {
            throw new Error('Request timeout');
          }
          
          // Don't retry on authentication errors
          if (error.message.includes('401') || error.message.includes('403')) {
            throw error;
          }
        }

        // If this is the last attempt, throw the error
        if (attempt === retries) {
          break;
        }

        // Wait before retrying
        await this.delay(this.retryDelay * (attempt + 1));
      }
    }

    throw lastError;
  }

  // HTTP methods
  async get<T>(endpoint: string, params?: Record<string, string | number | boolean>): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, { method: 'GET', params: params || {} });
  }

  async post<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, { method: 'POST', body });
  }

  async put<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, { method: 'PUT', body });
  }

  async patch<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, { method: 'PATCH', body });
  }

  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, { method: 'DELETE' });
  }

  // File upload
  async uploadFile<T>(
    endpoint: string,
    file: File,
    additionalData?: Record<string, unknown>
  ): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append('file', file);

    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, String(value));
      });
    }

    const headers = { ...this.getDefaultHeaders() };
    delete headers['Content-Type']; // Let browser set the boundary

    const response = await fetch(this.buildURL(endpoint), {
      method: 'POST',
      headers,
      body: formData,
      signal: AbortSignal.timeout(this.timeout),
    });

    return this.handleResponse<T>(response);
  }

  // Stream responses (for chat)
  async stream(
    endpoint: string,
    body: unknown,
    onMessage: (data: string) => void,
    onError?: (error: Error) => void,
    onComplete?: () => void
  ): Promise<void> {
    const url = this.buildURL(endpoint);
    const headers = this.getDefaultHeaders();

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        signal: AbortSignal.timeout(this.timeout),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('Response body is not readable');
      }

      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          onComplete?.();
          break;
        }

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') {
              onComplete?.();
              return;
            }
            onMessage(data);
          }
        }
      }
    } catch (error) {
      onError?.(error instanceof Error ? error : new Error(String(error)));
    }
  }

  // Health check
  async healthCheck(): Promise<boolean> {
    try {
      await this.get('/');
      return true;
    } catch {
      return false;
    }
  }

  // Clear auth token
  clearAuth(): void {
    localStorage.removeItem('civista_auth_token');
  }

  // Set auth token
  setAuth(token: string): void {
    localStorage.setItem('civista_auth_token', token);
  }
}

// Create singleton instance
export const apiClient = new ApiClient();

// Convenience functions
export const api = {
  get: <T>(endpoint: string, params?: Record<string, string | number | boolean>) => 
    apiClient.get<T>(endpoint, params),
  
  post: <T>(endpoint: string, body?: unknown) => 
    apiClient.post<T>(endpoint, body),
  
  put: <T>(endpoint: string, body?: unknown) => 
    apiClient.put<T>(endpoint, body),
  
  patch: <T>(endpoint: string, body?: unknown) => 
    apiClient.patch<T>(endpoint, body),
  
  delete: <T>(endpoint: string) => 
    apiClient.delete<T>(endpoint),
  
  upload: <T>(endpoint: string, file: File, data?: Record<string, unknown>) => 
    apiClient.uploadFile<T>(endpoint, file, data),
  
  stream: (
    endpoint: string,
    body: unknown,
    onMessage: (data: string) => void,
    onError?: (error: Error) => void,
    onComplete?: () => void
  ) => apiClient.stream(endpoint, body, onMessage, onError, onComplete),
  
  healthCheck: () => apiClient.healthCheck(),
  clearAuth: () => apiClient.clearAuth(),
  setAuth: (token: string) => apiClient.setAuth(token),
};